/**
 * TODO: Itterate recursively through the form value and return a comprehendable MetadataValue for complex fields
 *
 * NOTE: now we call this in complex-input-field.component.ts
 *  Probably better to do this in the FormMetadataHelper when splitting data
 */

import { BaseNestedFieldDefinition, FieldType } from '../../../core/field-definitions/entities/field-definition.entity';
import { FieldDefinitionHelper } from '../../../core/field-definitions/helpers/field-definition.helper';
import { TypedValue } from '../../../form-controls/components/form-typed-value-list/typed-values.entity';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { ComplexMapFieldValue, FieldValue } from '../../../profile-form/entities/field-values/field-type-values.entity';

// Possible returned types
type ValidFormValue =
    | FormValue<any>
    | FormValue<any>[]
    | Record<string, FormValue<any> | FormValue<any>[] | null>
    | null;
// Form group of possible returned types
export type FormGroupValue = Record<string, ValidFormValue>;

export class ComplexFormValueHelper {
    public static toMetadataValues(
        formGroupValues: FormGroupValue,
        complexFieldDefinition: BaseNestedFieldDefinition
    ): FormValue<ComplexMapFieldValue>[] {
        const setValues: FormValue<ComplexMapFieldValue> = this.generatePartialMetadata(
            formGroupValues,
            complexFieldDefinition,
            FormValueState.SET
        );
        const unsetValues: FormValue<ComplexMapFieldValue> = this.generatePartialMetadata(
            formGroupValues,
            complexFieldDefinition,
            FormValueState.UNSET
        );

        return [setValues, unsetValues];
    }

    private static generatePartialMetadata(
        formGroupValues: FormGroupValue,
        complexFieldDefinition: BaseNestedFieldDefinition,
        state: FormValueState
    ): FormValue<ComplexMapFieldValue> {
        let partialMetadata: ComplexMapFieldValue = {};

        this.complexFormValueToRecordValue(partialMetadata, formGroupValues, complexFieldDefinition, state);

        return new FormValue(partialMetadata, state);
    }

    // Itterates over keys of object and returns comprehensible MetadataValue for each key
    // Values are directly added to provided partialMetadata object
    private static complexFormValueToRecordValue(
        partialMetadata: ComplexMapFieldValue,
        formGroupValues: FormGroupValue,
        parentFieldDefinition: BaseNestedFieldDefinition,
        state: FormValueState
    ): void {
        Object.keys(formGroupValues).forEach(key => {
            // Extract value from form values
            this.childFormValueToRecordValue(partialMetadata, key, formGroupValues[key], parentFieldDefinition, state);
        });
    }

    private static childFormValueToRecordValue(
        partialMetadata: ComplexMapFieldValue,
        key: string,
        formValue: ValidFormValue,
        parentFieldDefinition: BaseNestedFieldDefinition,
        state: FormValueState
    ): void {
        const shortenedKey = key.split('.').pop() ?? key;

        const childFieldDefinition = parentFieldDefinition.Children?.find(field => field.Key === shortenedKey);

        if (!childFieldDefinition) {
            return;
        }

        if (FieldDefinitionHelper.IsElementary(childFieldDefinition)) {
            this.formValueToSimpleValue(partialMetadata, shortenedKey, formValue, state, childFieldDefinition);
            return;
        }

        if (FieldDefinitionHelper.IsElementaryList(childFieldDefinition)) {
            partialMetadata[shortenedKey] = this.formValueToListValue(formValue, state, childFieldDefinition);
            return;
        }

        if (FieldDefinitionHelper.IsComplex(childFieldDefinition)) {
            this.handleComplexFieldValue(partialMetadata, shortenedKey, formValue, childFieldDefinition, state);
            return;
        }

        partialMetadata[shortenedKey] = this.formValueToMultiItemValue(formValue, state, childFieldDefinition);
    }

    private static handleComplexFieldValue(
        partialMetadata: ComplexMapFieldValue,
        shortenedKey: string,
        formValue: ValidFormValue,
        childFieldDefinition: BaseNestedFieldDefinition,
        state: FormValueState
    ): void {
        if (formValue === null) {
            partialMetadata[shortenedKey] = null;
            return;
        }

        if (Array.isArray(formValue)) {
            this.handleComplexArrayValue(partialMetadata, shortenedKey, formValue, childFieldDefinition, state);
            return;
        }

        if (formValue instanceof FormValue) {
            console.error(
                `Single FormValue passed from complex field ${childFieldDefinition.DottedKey}. This should not happen. Returning null.`
            );
            partialMetadata[shortenedKey] = null;
            return;
        }

        const subPartialMetadata: ComplexMapFieldValue = {};
        this.complexFormValueToRecordValue(subPartialMetadata, formValue, childFieldDefinition, state);
        partialMetadata[shortenedKey] = subPartialMetadata;
    }

    private static handleComplexArrayValue(
        partialMetadata: ComplexMapFieldValue,
        shortenedKey: string,
        formValue: ValidFormValue,
        childFieldDefinition: BaseNestedFieldDefinition,
        state: FormValueState
    ): void {
        if (childFieldDefinition.Type === FieldType.ComplexListField) {
            partialMetadata[shortenedKey] = {
                [childFieldDefinition.Children![0].Key]: this.formValueToComplexListValue(
                    formValue,
                    state,
                    childFieldDefinition
                )
            };
            return;
        }

        if (childFieldDefinition.Type === FieldType.ThesaurusField) {
            partialMetadata[shortenedKey] = this.formValueToThesaurusValue(formValue, state, childFieldDefinition);
            return;
        }

        if (FieldDefinitionHelper.IsMap(childFieldDefinition)) {
            partialMetadata[shortenedKey] = this.formValueToMultiItemValue(formValue, state, childFieldDefinition);
            return;
        }

        console.error(
            `Array passed from complex field ${childFieldDefinition.DottedKey}. This should not happen. Returning null.`
        );
        partialMetadata[shortenedKey] = null;
    }

    private static formValueToSimpleValue(
        partialMetadata: ComplexMapFieldValue,
        shortenedKey: string,
        formValue: ValidFormValue,
        state: FormValueState,
        fieldDefinition: BaseNestedFieldDefinition
    ): void {
        if (state === FormValueState.UNSET || state === FormValueState.INDETERMINATE) {
            return;
        }

        // To research: when to add null values and when not to... Adding for now
        if (!formValue) {
            return;
        }

        if (Array.isArray(formValue)) {
            const areFormValues: boolean = formValue.every(formValue => formValue instanceof FormValue);

            let valuesOfState: FieldValue[] = formValue as unknown as FieldValue[];

            if (areFormValues) {
                valuesOfState = formValue
                    .filter(formValue => formValue.state === state)
                    .map((formValue: FormValue) => FormValue.unwrap(formValue));
            }

            if (valuesOfState.length == 0) {
                return;
            }

            console.warn(
                `ComplexFormValueHelper:: formValueToSimpleValue: Returned formValue is not a single value for field ${fieldDefinition.DottedKey}. We only return the first value.`
            );

            partialMetadata[shortenedKey] = valuesOfState[0];
            return;
        }

        if (formValue instanceof FormValue) {
            if (formValue.state !== state) {
                return;
            }
            partialMetadata[shortenedKey] = FormValue.unwrap(formValue);
            return;
        }

        console.error(
            `ComplexFormValueHelper:: formValueToSimpleValue: Returned formValue is not a valid value for field ${fieldDefinition.DottedKey}. This should not occur. Returning null.`
        );
    }

    private static formValueToListValue(
        formValue: ValidFormValue,
        state: FormValueState,
        listFieldDefinition: BaseNestedFieldDefinition
    ): Record<string, FieldValue[]> {
        if (!listFieldDefinition.Children) {
            console.error(
                `ComplexFormValueHelper:: formValueToListValue: FieldDefinition ${listFieldDefinition.DottedKey} does not have any child definitions.`
            );

            return {};
        }

        const listSubFieldKey: string = listFieldDefinition.Children[0].Key;
        const listFieldValue: Record<string, FieldValue[]> = {};

        // To research: Doublecheck if returning empty array is correct
        if (!formValue) {
            listFieldValue[listSubFieldKey] = [];
            return listFieldValue;
        }

        // Should always be an array value.
        if (Array.isArray(formValue)) {
            const areFormValues: boolean = formValue.every(formValue => formValue instanceof FormValue);

            let valuesOfState: FieldValue[] = formValue as unknown as FieldValue[];

            if (areFormValues) {
                valuesOfState = formValue
                    .filter(formValue => formValue.state === state)
                    .map((formValue: FormValue) => FormValue.unwrap(formValue));
            }

            listFieldValue[listSubFieldKey] = valuesOfState;
            return listFieldValue;
        }

        // Should never not be an array value
        // Return empty array

        console.error(
            `ComplexFormValueHelper:: formValueToListValue: Returned formValue is not an array for field ${listFieldDefinition.DottedKey}. This should not occur. Returning empty array.`
        );

        listFieldValue[listSubFieldKey] = [];
        return listFieldValue;
    }

    private static formValueToThesaurusValue(
        formValue: ValidFormValue,
        state: FormValueState,
        thesaurusFieldDefinition: BaseNestedFieldDefinition
    ): FieldValue {
        let thesaurusFieldValue: FieldValue = null;

        if (!formValue) {
            return thesaurusFieldValue;
        }

        // Should always be an array value.
        if (Array.isArray(formValue)) {
            const areFormValues: boolean = formValue.every(formValue => formValue instanceof FormValue);

            let valuesOfState: FieldValue[] = formValue as unknown as FieldValue[];

            if (areFormValues) {
                valuesOfState = formValue
                    .filter(formValue => formValue.state === state)
                    .map((formValue: FormValue) => formValue.value);
            }

            if (valuesOfState.length > 0) {
                thesaurusFieldValue = valuesOfState[0];
            }
        }

        return thesaurusFieldValue;
    }

    private static formValueToMultiItemValue(
        formValue: ValidFormValue,
        state: FormValueState,
        multiItemFieldDefinition: BaseNestedFieldDefinition
    ): Record<string, FieldValue[]> {
        const multiItemFieldValue: Record<string, FieldValue[]> = {};

        if (!formValue) {
            return multiItemFieldValue;
        }

        // Should always be an array value.
        if (Array.isArray(formValue)) {
            const areFormValues: boolean = formValue.every(formValue => formValue instanceof FormValue);

            let valuesOfState: FieldValue[] = formValue as unknown as FieldValue[];

            if (areFormValues) {
                valuesOfState = formValue
                    .filter(formValue => formValue.state === state)
                    .map((formValue: FormValue) => FormValue.unwrap(formValue));
            }

            valuesOfState.forEach(value => {
                if (!value) {
                    return;
                }

                const typedValue: TypedValue = value as unknown as TypedValue;

                if (!multiItemFieldValue[typedValue.type]) {
                    multiItemFieldValue[typedValue.type] = [typedValue.value];
                } else {
                    multiItemFieldValue[typedValue.type].push(typedValue.value);
                }
            });

            return multiItemFieldValue;
        }

        // Should never not be an array value
        // Return empty array

        console.error(
            `ComplexFormValueHelper:: formValueToMultiItemValue: Returned formValue is not an array for field ${multiItemFieldDefinition.DottedKey}. This should not occur. Returning empty array.`
        );

        return multiItemFieldValue;
    }

    private static formValueToComplexListValue(
        formValue: ValidFormValue,
        state: FormValueState,
        complexListFieldDefinition: BaseNestedFieldDefinition
    ): FieldValue[] {
        const complexListFieldValue: FieldValue[] = [];

        if (!formValue) {
            return complexListFieldValue;
        }

        // Should always be an array value.
        if (Array.isArray(formValue)) {
            const areFormValues: boolean = formValue.every(formValue => formValue instanceof FormValue);

            let valuesOfState: FieldValue[] = formValue as unknown as FieldValue[];

            if (areFormValues) {
                valuesOfState = formValue
                    .filter(formValue => formValue.state === state)
                    .map((formValue: FormValue) => FormValue.unwrap(formValue));
            }

            return valuesOfState;
        }

        // Should never not be an array value
        // Return empty array

        console.error(
            `ComplexFormValueHelper:: formValueToComplexListValue: Returned formValue is not an array for field ${complexListFieldDefinition.DottedKey}. This should not occur. Returning empty array.`
        );

        return [];
    }
}
